import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isAfter,
  isBefore,
  parseISO,
  isValid,
} from 'date-fns';
import { cn } from '@/lib/cn';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_14_days', label: 'Last 14 days' },
  { value: 'last_28_days', label: 'Last 28 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
] as const;

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DROPDOWN_WIDTH = 340;

/** Format a start–end date pair, collapsing to a single date when both are the same day. */
function formatDateRange(start: string, end: string): string {
  const s = parseISO(start);
  const e = parseISO(end);
  if (!isValid(s) || !isValid(e)) return `${start} – ${end}`;
  if (isSameDay(s, e)) return format(s, 'MMM d, yyyy');
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  if (sameMonth) return `${format(s, 'MMM d')} – ${format(e, 'd, yyyy')}`;
  if (sameYear) return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
  return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
}

interface ComparisonProps {
  preset: string;
  start: string;
  end: string;
  onPresetChange: (preset: string) => void;
  onDateChange: (start: string, end: string) => void;
  onClear: () => void;
  /** When true, the Compare tab shows a single-date picker (for time series comparison). */
  singleDate?: boolean;
}

interface DatePresetPickerProps {
  value: string;
  onChange: (preset: string) => void;
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  /** When provided, adds a "Compare" tab to the dropdown */
  comparison?: ComparisonProps;
}

/**
 * Date range picker with presets and a visual calendar for custom ranges.
 * Rendered via portal to avoid clipping by parent overflow.
 */
export function DatePresetPicker({
  value,
  onChange,
  startDate,
  endDate,
  onDateChange,
  comparison,
}: DatePresetPickerProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'range' | 'compare'>('range');
  const [subMode, setSubMode] = useState<'preset' | 'custom'>(value ? 'preset' : 'custom');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Calendar state
  const [viewMonth, setViewMonth] = useState(() => {
    if (startDate) {
      const d = parseISO(startDate);
      return isValid(d) ? d : new Date();
    }
    return new Date();
  });
  const [picking, setPicking] = useState<'start' | 'end'>('start');
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const parsedStart = useMemo(() => {
    if (!startDate) return null;
    const d = parseISO(startDate);
    return isValid(d) ? d : null;
  }, [startDate]);

  const parsedEnd = useMemo(() => {
    if (!endDate) return null;
    const d = parseISO(endDate);
    return isValid(d) ? d : null;
  }, [endDate]);

  const activePreset = DATE_PRESETS.find((p) => p.value === value);

  const hasComparison = !!(comparison && (comparison.preset || comparison.start));

  const displayLabel = useMemo(() => {
    let primary: string;
    if (activePreset) {
      primary = activePreset.label;
    } else if (startDate && endDate) {
      primary = formatDateRange(startDate, endDate);
    } else {
      primary = 'Select date range';
    }

    if (hasComparison) {
      const cmpObj = DATE_PRESETS.find((p) => p.value === comparison!.preset);
      let cmpLabel = '';
      if (cmpObj) {
        cmpLabel = cmpObj.label;
      } else if (comparison!.start && comparison!.end) {
        cmpLabel = formatDateRange(comparison!.start, comparison!.end);
      }
      if (cmpLabel) return `${primary}  vs  ${cmpLabel}`;
    }
    return primary;
  }, [activePreset, startDate, endDate, hasComparison, comparison]);

  const dropdownWidth = DROPDOWN_WIDTH;

  // Position the dropdown below the trigger, right-aligned if it would overflow
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const w = DROPDOWN_WIDTH;
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight < w ? rect.right - w : rect.left;
    setPos({ top: rect.bottom + 4, left: Math.max(8, left) });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Reposition when tab/subMode changes
  useEffect(() => {
    if (open) updatePosition();
  }, [tab, subMode, open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Reset calendar state when opening in custom mode
  useEffect(() => {
    if (open && tab === 'range' && subMode === 'custom') {
      if (parsedStart) {
        setViewMonth(parsedStart);
      }
      setPicking(parsedStart && parsedEnd ? 'start' : 'start');
    }
  }, [open, tab, subMode, parsedStart, parsedEnd]);

  const handleDayClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');

    // Clear preset whenever a custom day is picked
    onChange('');

    if (picking === 'start') {
      onDateChange(dateStr, endDate);
      setPicking('end');
    } else {
      // If picked end is before start, swap
      if (parsedStart && isBefore(day, parsedStart)) {
        onDateChange(dateStr, startDate);
      } else {
        onDateChange(startDate, dateStr);
      }
      setPicking('start');
    }
  };

  const handleApply = () => {
    if (startDate && endDate) {
      // Ensure preset is cleared so the label shows the custom range
      onChange('');
      onDateChange(startDate, endDate);
      setOpen(false);
    }
  };

  return (
    <div className="w-full">
      <label className="block text-xs font-medium text-slate-600 mb-1.5">
        Date Range
      </label>

      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full min-h-[36px] rounded-lg border bg-white px-3 py-1.5 text-left',
          'flex items-center gap-2 text-sm',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
          open ? 'border-brand-400 ring-2 ring-brand-500/20' : 'border-slate-200 hover:border-slate-300',
        )}
      >
        <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
        <span className={cn('flex-1 leading-snug', !activePreset && !startDate && 'text-slate-400')}>
          {displayLabel}
        </span>
        {hasComparison && (
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
        )}
        <ChevronDown className={cn(
          'w-4 h-4 text-slate-400 shrink-0 transition-transform duration-150',
          open && 'rotate-180',
        )} />
      </button>

      {/* Dropdown — rendered via portal to prevent clipping */}
      {createPortal(
        <AnimatePresence>
          {open && pos && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="fixed z-[9999] bg-white rounded-xl border border-slate-200 shadow-soft-md overflow-hidden"
              style={{ top: pos.top, left: pos.left, width: dropdownWidth }}
            >
              {/* Top-level tabs: Date Range | Compare */}
              {comparison && (
                <div className="flex border-b border-slate-100">
                  <button
                    onClick={() => setTab('range')}
                    className={cn(
                      'flex-1 py-2.5 text-xs font-medium transition-colors',
                      tab === 'range'
                        ? 'text-brand-600 border-b-2 border-brand-500'
                        : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    Date Range
                  </button>
                  <button
                    onClick={() => setTab('compare')}
                    className={cn(
                      'flex-1 py-2.5 text-xs font-medium transition-colors relative',
                      tab === 'compare'
                        ? 'text-brand-600 border-b-2 border-brand-500'
                        : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    Compare
                    {hasComparison && (
                      <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    )}
                  </button>
                </div>
              )}

              {tab === 'compare' && comparison ? (
                <ComparePanel
                  comparison={comparison}
                  onClose={() => setOpen(false)}
                />
              ) : (
                <RangePanel
                  subMode={subMode}
                  onSubModeChange={setSubMode}
                  activePreset={value}
                  onPresetSelect={(preset) => { onChange(preset); setOpen(false); }}
                  parsedStart={parsedStart}
                  parsedEnd={parsedEnd}
                  picking={picking}
                  onPickingChange={setPicking}
                  viewMonth={viewMonth}
                  onViewMonthChange={setViewMonth}
                  hoverDate={hoverDate}
                  onHoverDate={setHoverDate}
                  onDayClick={handleDayClick}
                  onApply={handleApply}
                  canApply={!!(startDate && endDate)}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

// ─── Calendar Grid ──────────────────────────────────────────────────────────

interface CalendarGridProps {
  viewMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  hoverDate: Date | null;
  picking: 'start' | 'end';
  onDayClick: (day: Date) => void;
  onDayHover: (day: Date | null) => void;
  onMouseLeave: () => void;
}

function CalendarGrid({
  viewMonth,
  onPrevMonth,
  onNextMonth,
  rangeStart,
  rangeEnd,
  hoverDate,
  picking,
  onDayClick,
  onDayHover,
  onMouseLeave,
}: CalendarGridProps) {
  const weeks = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);

    const rows: Date[][] = [];
    let current = calStart;
    let week: Date[] = [];

    while (!isAfter(current, calEnd)) {
      week.push(current);
      if (week.length === 7) {
        rows.push(week);
        week = [];
      }
      current = addDays(current, 1);
    }
    return rows;
  }, [viewMonth]);

  const today = useMemo(() => new Date(), []);

  return (
    <div onMouseLeave={onMouseLeave}>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={onPrevMonth}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-slate-800">
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          onClick={onNextMonth}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center text-[10px] font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {weeks.map((week, wi) =>
          week.map((day, di) => {
            const inMonth = isSameMonth(day, viewMonth);
            const isToday = isSameDay(day, today);
            const isStart = rangeStart ? isSameDay(day, rangeStart) : false;
            const isEnd = rangeEnd ? isSameDay(day, rangeEnd) : false;
            const isEndpoint = isStart || isEnd;

            // Determine if day is in range
            let inRange = false;
            if (rangeStart && rangeEnd) {
              inRange = isAfter(day, rangeStart) && isBefore(day, rangeEnd);
            }

            // Hover preview: show tentative range while picking
            let inHoverRange = false;
            if (hoverDate && picking === 'end' && rangeStart && !rangeEnd) {
              const hEnd = isAfter(hoverDate, rangeStart) ? hoverDate : rangeStart;
              const hStart = isAfter(hoverDate, rangeStart) ? rangeStart : hoverDate;
              inHoverRange = (isAfter(day, hStart) || isSameDay(day, hStart)) &&
                             (isBefore(day, hEnd) || isSameDay(day, hEnd));
            }

            // Edge rounding for range backgrounds
            const isRangeStart = isStart && (inRange || (rangeEnd && !isSameDay(rangeStart!, rangeEnd)));
            const isRangeEnd = isEnd && (inRange || (rangeStart && !isSameDay(rangeStart!, rangeEnd!)));

            return (
              <button
                key={`${wi}-${di}`}
                type="button"
                onClick={() => onDayClick(day)}
                onMouseEnter={() => onDayHover(day)}
                className={cn(
                  'relative h-8 text-xs font-medium transition-colors',
                  // Range background (full cell)
                  (inRange || (isStart && isRangeStart) || (isEnd && isRangeEnd)) && 'bg-brand-50',
                  inHoverRange && !inRange && !isEndpoint && 'bg-brand-50/50',
                  // Range edge rounding
                  isRangeStart && 'rounded-l-full',
                  isRangeEnd && 'rounded-r-full',
                  // Hover range edge rounding
                  inHoverRange && !inRange && isSameDay(day, rangeStart!) && 'rounded-l-full bg-brand-50/50',
                  inHoverRange && !inRange && hoverDate && isSameDay(day, hoverDate) && 'rounded-r-full',
                )}
              >
                <span
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full mx-auto transition-colors',
                    !inMonth && 'text-slate-300',
                    inMonth && !isEndpoint && 'text-slate-700 hover:bg-slate-100',
                    isToday && !isEndpoint && 'font-bold text-brand-600',
                    isEndpoint && 'bg-brand-500 text-white font-semibold',
                    inHoverRange && !isEndpoint && !inRange && inMonth && 'text-brand-700',
                  )}
                >
                  {format(day, 'd')}
                </span>
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

// ─── Range Panel (unified presets + custom in one view) ──────────────────────

function RangePanel({
  subMode,
  onSubModeChange,
  activePreset,
  onPresetSelect,
  parsedStart,
  parsedEnd,
  picking,
  onPickingChange,
  viewMonth,
  onViewMonthChange,
  hoverDate,
  onHoverDate,
  onDayClick,
  onApply,
  canApply,
}: {
  subMode: 'preset' | 'custom';
  onSubModeChange: (m: 'preset' | 'custom') => void;
  activePreset: string;
  onPresetSelect: (preset: string) => void;
  parsedStart: Date | null;
  parsedEnd: Date | null;
  picking: 'start' | 'end';
  onPickingChange: (p: 'start' | 'end') => void;
  viewMonth: Date;
  onViewMonthChange: (d: Date) => void;
  hoverDate: Date | null;
  onHoverDate: (d: Date | null) => void;
  onDayClick: (day: Date) => void;
  onApply: () => void;
  canApply: boolean;
}) {
  return (
    <div>
      {/* Sub-mode pills */}
      <div className="flex items-center gap-1 px-3 pt-3 pb-2">
        <button
          onClick={() => onSubModeChange('preset')}
          className={cn(
            'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
            subMode === 'preset'
              ? 'bg-brand-50 text-brand-700'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50',
          )}
        >
          Presets
        </button>
        <button
          onClick={() => onSubModeChange('custom')}
          className={cn(
            'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
            subMode === 'custom'
              ? 'bg-brand-50 text-brand-700'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50',
          )}
        >
          Custom
        </button>
      </div>

      {subMode === 'preset' ? (
        <div className="py-1 max-h-56 overflow-y-auto">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => onPresetSelect(preset.value)}
              className={cn(
                'w-full px-3 py-2 text-sm text-left transition-colors',
                activePreset === preset.value
                  ? 'bg-brand-50 text-brand-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="px-3 pb-3 space-y-3">
          {/* Selection indicator */}
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => onPickingChange('start')}
              className={cn(
                'flex-1 px-2.5 py-1.5 rounded-lg border text-center transition-colors',
                picking === 'start'
                  ? 'border-brand-400 bg-brand-50 text-brand-700 font-medium ring-1 ring-brand-200'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {parsedStart ? format(parsedStart, 'MMM d, yyyy') : 'Start date'}
            </button>
            <span className="text-slate-300">→</span>
            <button
              onClick={() => onPickingChange('end')}
              className={cn(
                'flex-1 px-2.5 py-1.5 rounded-lg border text-center transition-colors',
                picking === 'end'
                  ? 'border-brand-400 bg-brand-50 text-brand-700 font-medium ring-1 ring-brand-200'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {parsedEnd ? format(parsedEnd, 'MMM d, yyyy') : 'End date'}
            </button>
          </div>

          <CalendarGrid
            viewMonth={viewMonth}
            onPrevMonth={() => onViewMonthChange(subMonths(viewMonth, 1))}
            onNextMonth={() => onViewMonthChange(addMonths(viewMonth, 1))}
            rangeStart={parsedStart}
            rangeEnd={parsedEnd}
            hoverDate={hoverDate}
            picking={picking}
            onDayClick={onDayClick}
            onDayHover={onHoverDate}
            onMouseLeave={() => onHoverDate(null)}
          />

          <button
            onClick={onApply}
            disabled={!canApply}
            className={cn(
              'w-full h-8 rounded-lg text-sm font-medium transition-colors',
              canApply
                ? 'bg-brand-500 text-white hover:bg-brand-600'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed',
            )}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Compare Panel (inside DatePresetPicker dropdown) ────────────────────────

function ComparePanel({
  comparison,
  onClose,
}: {
  comparison: ComparisonProps;
  onClose: () => void;
}) {
  const singleDate = !!comparison.singleDate;
  const [cmpMode, setCmpMode] = useState<'preset' | 'custom'>(singleDate ? 'custom' : 'preset');

  // Calendar state for custom comparison range
  const [viewMonth, setViewMonth] = useState(() => {
    if (comparison.start) {
      const d = parseISO(comparison.start);
      return isValid(d) ? d : new Date();
    }
    return new Date();
  });
  const [picking, setPicking] = useState<'start' | 'end'>('start');
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const parsedStart = useMemo(() => {
    if (!comparison.start) return null;
    const d = parseISO(comparison.start);
    return isValid(d) ? d : null;
  }, [comparison.start]);

  const parsedEnd = useMemo(() => {
    if (!comparison.end) return null;
    const d = parseISO(comparison.end);
    return isValid(d) ? d : null;
  }, [comparison.end]);

  // Single-date click: set both start and end to the same date, close
  const handleSingleDayClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    comparison.onPresetChange('');
    comparison.onDateChange(dateStr, dateStr);
    onClose();
  };

  // Range click (pivot mode)
  const handleDayClick = (day: Date) => {
    if (singleDate) {
      handleSingleDayClick(day);
      return;
    }
    const dateStr = format(day, 'yyyy-MM-dd');
    comparison.onPresetChange('');
    if (picking === 'start') {
      comparison.onDateChange(dateStr, comparison.end);
      setPicking('end');
    } else {
      if (parsedStart && isBefore(day, parsedStart)) {
        comparison.onDateChange(dateStr, comparison.start);
      } else {
        comparison.onDateChange(comparison.start, dateStr);
      }
      setPicking('start');
      onClose();
    }
  };

  const handleApply = () => {
    if (comparison.start && comparison.end) {
      comparison.onPresetChange('');
      comparison.onDateChange(comparison.start, comparison.end);
      onClose();
    }
  };

  const hasValue = !!(comparison.preset || comparison.start);

  return (
    <div>
      {/* Sub-mode: preset vs custom (hidden for single-date mode) */}
      <div className="flex items-center gap-1 px-3 pt-3 pb-2">
        {!singleDate && (
          <>
            <button
              onClick={() => setCmpMode('preset')}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
                cmpMode === 'preset'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50',
              )}
            >
              Presets
            </button>
            <button
              onClick={() => setCmpMode('custom')}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
                cmpMode === 'custom'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50',
              )}
            >
              Custom
            </button>
          </>
        )}
        {singleDate && (
          <span className="text-[11px] font-medium text-slate-500">Pick a start date</span>
        )}
        <div className="flex-1" />
        {hasValue && (
          <button
            onClick={() => { comparison.onClear(); onClose(); }}
            className="text-[11px] font-medium text-red-400 hover:text-red-600 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {cmpMode === 'preset' ? (
        <div className="py-1 max-h-56 overflow-y-auto">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => {
                comparison.onPresetChange(preset.value);
                onClose();
              }}
              className={cn(
                'w-full px-3 py-2 text-sm text-left transition-colors',
                comparison.preset === preset.value
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : singleDate ? (
        /* Single-date calendar for time series comparison */
        <div className="px-3 pb-3 space-y-3">
          <div className="text-xs text-center text-slate-500 py-1">
            {parsedStart
              ? <span className="font-medium text-indigo-700">{format(parsedStart, 'MMM d, yyyy')}</span>
              : 'Pick a comparison start date'}
          </div>

          <CalendarGrid
            viewMonth={viewMonth}
            onPrevMonth={() => setViewMonth(subMonths(viewMonth, 1))}
            onNextMonth={() => setViewMonth(addMonths(viewMonth, 1))}
            rangeStart={parsedStart}
            rangeEnd={parsedStart}
            hoverDate={null}
            picking="start"
            onDayClick={handleSingleDayClick}
            onDayHover={() => {}}
            onMouseLeave={() => {}}
          />
        </div>
      ) : (
        <div className="px-3 pb-3 space-y-3">
          {/* Selection indicator */}
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setPicking('start')}
              className={cn(
                'flex-1 px-2.5 py-1.5 rounded-lg border text-center transition-colors',
                picking === 'start'
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700 font-medium ring-1 ring-indigo-200'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {parsedStart ? format(parsedStart, 'MMM d, yyyy') : 'Start date'}
            </button>
            <span className="text-slate-300">→</span>
            <button
              onClick={() => setPicking('end')}
              className={cn(
                'flex-1 px-2.5 py-1.5 rounded-lg border text-center transition-colors',
                picking === 'end'
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700 font-medium ring-1 ring-indigo-200'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {parsedEnd ? format(parsedEnd, 'MMM d, yyyy') : 'End date'}
            </button>
          </div>

          <CalendarGrid
            viewMonth={viewMonth}
            onPrevMonth={() => setViewMonth(subMonths(viewMonth, 1))}
            onNextMonth={() => setViewMonth(addMonths(viewMonth, 1))}
            rangeStart={parsedStart}
            rangeEnd={parsedEnd}
            hoverDate={hoverDate}
            picking={picking}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onMouseLeave={() => setHoverDate(null)}
          />

          <button
            onClick={handleApply}
            disabled={!comparison.start || !comparison.end}
            className={cn(
              'w-full h-8 rounded-lg text-sm font-medium transition-colors',
              comparison.start && comparison.end
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed',
            )}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
